import React from "react";
import {
  imgService4,
  imgService5,
  imgServiceBg,
  imgShipping,
} from "../../assets/imgExport";
import { IconChevronRight, IconDeployedCode } from "../../assets/svgExport";
import GetInTouch from "../../components/GetInTouch/GetInTouch";
import { NavLink } from "react-router-dom";

export default function ServicesPage() {
  function HeroSection() {
    return (
      <div className="hero-container">
        <p className="subtitle">Services</p>
        <h1>End-to-end marine transportation for offshore wind.</h1>
        <p className="paragraph">
          Venti Maris delivers complete marine transport solutions for offshore
          wind projects covering every stage from early planning to final
          delivery.
        </p>
      </div>
    );
  }

  function PlanningSection() {
    return (
      <div className="equal-flex">
        <div>
          <p className="subtitle">Smart planning. Reliable delivery.</p>
          <h2>Optimised planning & chartering</h2>
          <div>
            <p>
              We identify and secure the right vessels, routes and chartering
              frameworks to meet your project’s exact requirements.
            </p>
            <p>
              By combining direct vessel access in Asia with a deep
              understanding of European procurement, we deliver competitive
              pricing on familiar terms — without hidden surprises.
            </p>
            <p>Our chartering services include:</p>
            <ul className="list-disc list-inside flex flex-col gap-1">
              <li>Vessel sourcing and suitability assessments</li>
              <li>
                Contract structuring and negotiation (BIMCO HEAVYCON and bespoke
                frameworks)
              </li>
              <li>
                Programme optimisation to align transport with fabrication and
                installation schedules
              </li>
              <li>
                Insurance coordination and full Marine Warranty Survey (MWS)
                compliance
              </li>
            </ul>
            <p>
              The result: You get vessel access others can’t provide, at pricing
              that makes sense, with someone who understands your bigger picture
              challenges.
            </p>
            <p className="mt-10">
              <NavLink
                to="/contact"
                className="flex items-center gap-1 text-inherit-size"
              >
                Talk to us about planning & chartering
                <IconChevronRight className="w-4" />
              </NavLink>
            </p>
          </div>
        </div>
        <img src={imgService5} alt="wind-farm-shipping" />
      </div>
    );
  }

  function EngineeringSection() {
    return (
      <div className="equal-flex">
        <img
          src={imgService4}
          alt="wind-farm-shipping"
          className="order-2 md:order-1"
        />
        <div>
          <p className="subtitle">
            Marine Warranty-level assurance built in from the start
          </p>
          <h2>Advanced engineering solutions</h2>
          <p>
            Our engineering partnership with Empire Engineering provides the
            technical confidence developers need. Avoiding the need to find
            engineering consultants, managing another tender process, and
            coordinating between multiple parties.
          </p>
          <p>
            We supply all the necessary design and analysis support to ensure
            cargo arrives safely, securely and as expected.
          </p>
          <p>Capabilities include:</p>
          <ul className="list-disc list-inside flex flex-col gap-1">
            <li>Seafastening and grillage design</li>
            <li>Vessel stability and strength assessments</li>
            <li>Load-out/off-load engineering and feasibility studies</li>
            <li>Cargo fatigue and dynamic stress analysis</li>
            <li>Risk and safety reviews (HAZOP/HIRA)</li>
          </ul>
          <p>
            The result: fewer interfaces, faster approvals and complete
            confidence that every component will arrive as planned.
          </p>
          <p className="mt-10">
            <NavLink
              to="/contact"
              className="flex items-center gap-1 text-inherit-size"
            >
              Talk to us about advanced engineering solutions
              <IconChevronRight className="w-4" />
            </NavLink>
          </p>
        </div>
      </div>
    );
  }

  function ExecutionSection() {
    return (
      <div className="equal-flex">
        <div>
          <p className="subtitle">
            Managing complexity so you can focus on building
          </p>
          <h2>Seamless Execution</h2>
          <p>
            We take on the execution phase, owning all of the tasks that are
            critical but time-consuming, giving you back bandwidth to focus on
            what matters most.
          </p>
          <p>
            We ensure operations run smoothly, from loading and clearance to
            final delivery.
          </p>
          <p>Our execution services cover:</p>
          <ul className="list-disc list-inside flex flex-col gap-1">
            <li>Port and vessel-agency coordination</li>
            <li>Customs, insurance and documentation management</li>
            <li>
              On-site supervision during loading, seafastening and off-load
            </li>
            <li>Real-time communication and progress reporting</li>
          </ul>
          <p>
            The result: complete peace of mind that logistics are handled
            professionally without without surprise calls or last-minute crisis
            derailing your construction schedule.
          </p>
          <p className="mt-10">
            <NavLink
              to="/contact"
              className="flex items-center gap-1 text-inherit-size"
            >
              Talk to us about supporting on execution
              <IconChevronRight className="w-4" />
            </NavLink>
          </p>
        </div>
        <img src={imgServiceBg} alt="wind-farm-shipping" />
      </div>
    );
  }

  function WhatWeDoSection() {
    return (
      <div className="equal-flex">
        <div>
          <p className="subtitle">Shipping</p>
          <h2>What We Do</h2>
          <p>
            Venti Maris focuses exclusively on delivering end-to-end
            transportation solutions for:
          </p>
          <ul className="flex flex-col gap-4">
            <li className="flex gap-2 items-center">
              <IconDeployedCode className="w-4 h-4" />
              Foundations – monopiles, transition pieces, jackets, pin piles,
              floaters
            </li>
            <li className="flex gap-2 items-center">
              <IconDeployedCode className="w-4 h-4" />
              Turbines – towers, nacelles, blades
            </li>
            <li className="flex gap-2 items-center">
              <IconDeployedCode className="w-4 h-4" />
              Substations – topsides and foundations
            </li>
          </ul>
        </div>
        <img src={imgShipping} alt="wind-farm-shipping" />
      </div>
    );
  }

  return (
    <div>
      <HeroSection />
      <div className="body-container">
        <PlanningSection />
        <EngineeringSection />
        <ExecutionSection />
        <WhatWeDoSection />
        <GetInTouch
          text={{
            heading:
              "Plan your next offshore transport project with confidence.",
            paragraph:
              "Contact our team to discuss vessel options, delivery schedules and cost savings.",
          }}
        />
      </div>
    </div>
  );
}
